import {
  ChangeDetectionStrategy,
  Component,
  Signal,
  computed,
  effect,
  signal,
  inject,
  DestroyRef,
  input,
  output,
} from '@angular/core';
import { DatePipe, registerLocaleData } from '@angular/common';
import { FieldTree, FormField, form } from '@angular/forms/signals';
import {
  DropdownComponent,
  DropdownOption,
} from '../dropdown/dropdown.component';
import { NzTimePickerModule } from 'ng-zorro-antd/time-picker';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { SvgIconComponent } from 'angular-svg-icon';
import { AppButton, ButtonType } from '../../button/button.component';
import { NZ_I18N, NzI18nService, en_US } from 'ng-zorro-antd/i18n';
import en from '@angular/common/locales/en';

registerLocaleData(en);

import { TimezoneService } from '../../../../services/timezone.service';
import { TimeService } from '../../../../services/time.service';
import {
  constructISO8601E,
  convertOffsetToMs,
  datePipeFormatFull,
  getOffsetFromISO8601E,
  parseZonedTimestampMs,
  zonedWallClockDate,
} from '../../../../utils/timezone.utils';
import { logbookOffsets } from './date-time.types';
import { PresenceAvatarsComponent } from '../../presence-avatars/presence-avatars.component';
import { HubUser } from '../../../checklist/types/generated/checklist-models';

type Mode = 'date' | 'time' | 'datetime';

@Component({
  selector: 'app-date-time',
  imports: [
    FormField,
    DropdownComponent,
    NzTimePickerModule,
    NzDatePickerModule,
    SvgIconComponent,
    AppButton,
    PresenceAvatarsComponent,
  ],
  providers: [DatePipe, { provide: NZ_I18N, useValue: en_US }],
  templateUrl: './date-time.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateTimeComponent {
  private readonly datePipe = inject(DatePipe);
  private readonly timeService = inject(TimeService);
  private readonly timezoneService = inject(TimezoneService);
  private readonly i18n = inject(NzI18nService);
  private readonly destroyRef = inject(DestroyRef);
  readonly field = input.required<FieldTree<string | null>>();
  readonly id = input('');
  readonly label = input('');

  readonly presenceViewers = input<readonly HubUser[]>([]);
  readonly presenceInteracting = output<boolean>();

  readonly mode = input<Mode, unknown>('datetime', {
    transform: (value: unknown): Mode =>
      value === 'date' || value === 'time' || value === 'datetime'
        ? value
        : 'datetime',
  });
  readonly placeholder = input('');
  readonly min = input<string>();
  readonly max = input<string>();
  readonly stepMinutes = input(1);
  readonly showSeconds = input(false);
  readonly allowTzChange = input(true);
  readonly containerClass = input('');
  readonly inputClass = input('');
  readonly timePlaceholder = input('Select time');
  readonly datePlaceholder = input('Select date');
  readonly keyboardInput = input(false);
  // Whether the completion time was manually edited (entry_time set). Drives the clock-plus
  // indicator. An undo-preserved time is not a manual edit and must not light it up.
  readonly edited = input(false);

  protected readonly state = computed(() => this.field()());

  protected readonly labelText = computed(
    () => `${this.label()}${this.state().required() ? ' *' : ''}`
  );
  protected readonly timeFormat = computed(() =>
    this.showSeconds() ? 'HH:mm:ss' : 'HH:mm'
  );

  private readonly tz = this.timezoneService.timezone;
  private readonly now = this.timeService.currentTime;
  isEditing = signal(false);

  readonly tzOptions: DropdownOption[] = logbookOffsets.map(tz => ({
    label: tz.name,
    value: String(tz.offset),
  }));

  private readonly offset = signal<string | null>('0');
  readonly offsetField = form(this.offset);

  private readonly editorState = signal<{ time: Date | null; date: Date }>({
    time: new Date(),
    date: new Date(),
  });
  readonly editorForm = form(this.editorState);

  protected readonly ButtonType = ButtonType;
  displayText: Signal<string> = computed(() => {
    const v = this.state().value();
    if (!v) {
      return (
        this.datePipe.transform(
          this.now(),
          datePipeFormatFull(this.tz()),
          this.tz()
        ) ?? ''
      );
    }
    const tz = getOffsetFromISO8601E(v);
    // Derive the instant offset-aware because parsing the offset-stripped wall-clock string with
    // `new Date(...)` would be interpreted as browser-local time, double-applying `tz` below.
    // An unreadable value falls back to the placeholder — DatePipe throws on an Invalid Date, and
    // it throws here inside change detection, taking the whole view down with it.
    const epochMs = parseZonedTimestampMs(v);
    if (epochMs === null) return '';
    return (
      this.datePipe.transform(new Date(epochMs), datePipeFormatFull(tz), tz) ??
      ''
    );
  });

  private seededFrom: string | null = null;

  constructor() {
    this.i18n.setLocale(en_US);
    effect(() => {
      const raw = this.state().value();
      if (this.isEditing() && raw === this.seededFrom) return;
      this.seededFrom = raw;

      // An unparseable value populates from the current time because nz-date-picker renders an Invalid
      // Date as an empty field, and `onSave` throws out of `constructISO8601E`, leaving the
      // editor open with nothing written.
      const wallClock = zonedWallClockDate(raw);

      if (raw && wallClock) {
        const tz = getOffsetFromISO8601E(raw);
        this.editorState.set({ time: wallClock, date: wallClock });
        this.offset.set(String(convertOffsetToMs(tz)));
      } else {
        const tz = this.tz();
        const now = this.now();
        this.editorState.set({
          time: this.parseTimeValue(
            this.datePipe.transform(now, this.timeFormat(), tz)
          ),
          date: now,
        });
        this.offset.set(String(convertOffsetToMs(tz)));
      }
    });
  }

  private datePopupSwipeCleanup: (() => void) | null = null;

  onDatePickerOpenChange(isOpen: boolean): void {
    if (isOpen) {
      this.scheduleDatePopupSwipeAttach(0);
    } else {
      this.detachDatePopupSwipe();
    }
  }

  private scheduleDatePopupSwipeAttach(attempts: number): void {
    if (attempts >= 10) return;
    window.setTimeout(
      () => {
        if (
          !window.matchMedia('(max-width: 767px) and (orientation: portrait)')
            .matches
        ) {
          return;
        }
        const popup = document.querySelector<HTMLElement>('.clf-date-picker');
        const body = popup?.querySelector<HTMLElement>(
          '.ant-picker-date-panel .ant-picker-body'
        );
        if (!popup || !body) {
          this.scheduleDatePopupSwipeAttach(attempts + 1);
          return;
        }
        this.attachDatePopupSwipe(popup, body);
      },
      attempts === 0 ? 0 : 30
    );
  }

  private attachDatePopupSwipe(popup: HTMLElement, panel: HTMLElement): void {
    this.detachDatePopupSwipe();

    const COMMIT_RATIO = 0.25;
    const ANIMATION_MS = 220;
    const VERTICAL_TOLERANCE = 28;
    const HORIZONTAL_TRIGGER = 10;
    const CLICK_SUPPRESS_MS = 350;

    let startX = 0;
    let startY = 0;
    let panelWidth = 0;
    let direction: 1 | -1 = 1;
    let ghost: HTMLElement | null = null;
    let tracking = false;
    let active = false;
    let savedParentPosition = '';
    let savedParentOverflow = '';
    let suppressClickUntil = 0;
    const parent = panel.parentElement;

    const savedPanelTouchAction = panel.style.touchAction;
    const savedPanelUserSelect = panel.style.userSelect;
    panel.style.touchAction = 'pan-y';
    panel.style.userSelect = 'none';

    const resetPanel = (): void => {
      panel.style.transition = '';
      panel.style.transform = '';
      panel.style.willChange = '';
    };

    const tearDownGhost = (): void => {
      if (ghost) {
        ghost.remove();
        ghost = null;
      }
      if (parent) {
        parent.style.position = savedParentPosition;
        parent.style.overflow = savedParentOverflow;
      }
      active = false;
    };

    const startSwipe = (dx: number): void => {
      if (!parent) return;
      direction = dx < 0 ? 1 : -1;
      panelWidth = panel.offsetWidth;
      ghost = panel.cloneNode(true) as HTMLElement;
      ghost.style.pointerEvents = 'none';
      ghost.style.position = 'absolute';
      ghost.style.top = `${panel.offsetTop}px`;
      ghost.style.left = `${panel.offsetLeft}px`;
      ghost.style.width = `${panelWidth}px`;
      ghost.style.willChange = 'transform';
      savedParentPosition = parent.style.position;
      savedParentOverflow = parent.style.overflow;
      parent.style.position = parent.style.position || 'relative';
      parent.style.overflow = 'hidden';
      parent.appendChild(ghost);
      const navBtn =
        direction === 1
          ? popup.querySelector<HTMLButtonElement>(
              '.ant-picker-header-next-btn'
            )
          : popup.querySelector<HTMLButtonElement>(
              '.ant-picker-header-prev-btn'
            );
      navBtn?.click();
      panel.style.transition = 'none';
      panel.style.willChange = 'transform';
      panel.style.transform = `translateX(${direction * panelWidth}px)`;
      active = true;
    };

    const updateSwipe = (dx: number): void => {
      if (!active || !ghost) return;
      const clamped = Math.max(-panelWidth, Math.min(panelWidth, dx));
      ghost.style.transform = `translateX(${clamped}px)`;
      panel.style.transform = `translateX(${clamped + direction * panelWidth}px)`;
    };

    const finishAnimation = (
      ghostEnd: number,
      panelEnd: number,
      onDone: () => void
    ): void => {
      if (!ghost) {
        onDone();
        return;
      }
      ghost.style.transition = `transform ${ANIMATION_MS}ms ease-out`;
      panel.style.transition = `transform ${ANIMATION_MS}ms ease-out`;
      requestAnimationFrame(() => {
        if (ghost) ghost.style.transform = `translateX(${ghostEnd}px)`;
        panel.style.transform = `translateX(${panelEnd}px)`;
      });
      window.setTimeout(onDone, ANIMATION_MS + 40);
    };

    const commitSwipe = (): void => {
      finishAnimation(-direction * panelWidth, 0, () => {
        resetPanel();
        tearDownGhost();
      });
    };

    const cancelSwipe = (): void => {
      finishAnimation(0, direction * panelWidth, () => {
        const restoreBtn =
          direction === 1
            ? popup.querySelector<HTMLButtonElement>(
                '.ant-picker-header-prev-btn'
              )
            : popup.querySelector<HTMLButtonElement>(
                '.ant-picker-header-next-btn'
              );
        restoreBtn?.click();
        resetPanel();
        tearDownGhost();
      });
    };

    let pointerId: number | null = null;
    const onPointerDown = (event: PointerEvent): void => {
      if (!event.isPrimary || event.button !== 0 || active) return;
      startX = event.clientX;
      startY = event.clientY;
      pointerId = event.pointerId;
      tracking = true;
    };

    const onPointerMove = (event: PointerEvent): void => {
      if (!tracking || event.pointerId !== pointerId) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      if (!active) {
        if (Math.abs(dy) > VERTICAL_TOLERANCE) {
          tracking = false;
          return;
        }
        if (Math.abs(dx) < HORIZONTAL_TRIGGER) return;
        startSwipe(dx);
        try {
          panel.setPointerCapture(event.pointerId);
        } catch {
          /* no-op */
        }
      }
      updateSwipe(dx);
    };

    const onPointerUp = (event: PointerEvent): void => {
      if (!tracking || event.pointerId !== pointerId) return;
      tracking = false;
      pointerId = null;
      if (!active) return;
      suppressClickUntil = Date.now() + CLICK_SUPPRESS_MS;
      const dx = event.clientX - startX;
      if (Math.abs(dx) >= panelWidth * COMMIT_RATIO) {
        commitSwipe();
      } else {
        cancelSwipe();
      }
    };

    const onClickCapture = (event: MouseEvent): void => {
      if (active || Date.now() < suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    panel.addEventListener('pointerdown', onPointerDown);
    panel.addEventListener('pointermove', onPointerMove);
    panel.addEventListener('pointerup', onPointerUp);
    panel.addEventListener('pointercancel', onPointerUp);
    panel.addEventListener('click', onClickCapture, true);

    this.datePopupSwipeCleanup = () => {
      panel.removeEventListener('pointerdown', onPointerDown);
      panel.removeEventListener('pointermove', onPointerMove);
      panel.removeEventListener('pointerup', onPointerUp);
      panel.removeEventListener('pointercancel', onPointerUp);
      panel.removeEventListener('click', onClickCapture, true);
      panel.style.touchAction = savedPanelTouchAction;
      panel.style.userSelect = savedPanelUserSelect;
      resetPanel();
      tearDownGhost();
    };
    this.destroyRef.onDestroy(() => this.detachDatePopupSwipe());
  }

  private detachDatePopupSwipe(): void {
    this.datePopupSwipeCleanup?.();
    this.datePopupSwipeCleanup = null;
  }

  enableEdit() {
    if (this.state().disabled()) return;
    this.setEditing(true);
  }

  onUndo() {
    this.setEditing(false);
  }

  private setEditing(editing: boolean): void {
    this.isEditing.set(editing);
    this.presenceInteracting.emit(editing);
  }

  onSave() {
    const { time: rawTime, date: rawDate } = this.editorState();
    const offset = Number(this.offset());

    const d = new Date(rawDate);
    const utcDate = new Date(
      Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
    );

    let timeStr = this.formatTime(rawTime ?? new Date());
    if (this.mode() === 'date')
      timeStr = this.showSeconds() ? '00:00:00' : '00:00';

    const iso = constructISO8601E(utcDate, timeStr, offset);

    const min = this.min();
    const max = this.max();
    if (min && iso < min) {
      this.setEditing(false);
      return;
    }
    if (max && iso > max) {
      this.setEditing(false);
      return;
    }

    const state = this.state();
    state.value.set(iso);
    state.markAsDirty();
    state.markAsTouched();
    this.setEditing(false);
  }

  private parseTimeValue(value: string | null): Date | null {
    if (!value) return null;
    const [hours = '0', minutes = '0', seconds = '0'] = value.split(':');
    const time = new Date();
    time.setHours(Number(hours), Number(minutes), Number(seconds), 0);
    return time;
  }

  private formatTime(date: Date): string {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return this.showSeconds()
      ? `${hours}:${minutes}:${seconds}`
      : `${hours}:${minutes}`;
  }
}
